import React, { useEffect, useState } from 'react';
import {
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  Paper,
  Box,
  Chip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  useTheme,
  useMediaQuery,
  CircularProgress
} from '@material-ui/core';
import {
  FilterList,
  Refresh,
  PictureAsPdf
} from '@material-ui/icons';
import MaterialTable from 'material-table';
import { makeStyles } from "@material-ui/core/styles";
import { Autocomplete } from '@material-ui/lab';
import { useSelector } from 'react-redux';
import { toast } from "react-toastify";
import FournisseurService from '../../services/FournisseurService';
import BonService from '../../services/BonService';
import NumCommandeService from '../../services/NumCommandeService';
import 'jspdf-autotable';
import { generateCommandesPDF } from '../../utils/pdfUtils';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
    padding: theme.spacing(3),
  },
  header: {
    background: 'linear-gradient(135deg, #226D68 0%, #2a857e 100%)',
    color: 'white',
    borderRadius: '16px',
    padding: theme.spacing(4),
    marginBottom: theme.spacing(4),
    boxShadow: '0 8px 32px rgba(34, 109, 104, 0.2)',
  },
  title: {
    fontWeight: 700,
    fontSize: '2.5rem',
    textAlign: 'center',
    marginBottom: theme.spacing(2),
    background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  subtitle: {
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
    fontSize: '1.1rem',
    marginBottom: theme.spacing(3),
  },
  filtersContainer: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: '1px solid #e0e0e0',
  },
  filterSection: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  autocomplete: {
    minWidth: 250,
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
    },
  },
  select: {
    minWidth: 200,
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
    },
  },
  statsContainer: {
    marginBottom: theme.spacing(3),
  },
  statCard: {
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    borderRadius: '16px',
    padding: theme.spacing(2),
    textAlign: 'center',
    transition: 'all 0.3s ease',
    border: '1px solid #e0e0e0',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 25px rgba(34, 109, 104, 0.15)',
    },
  },
  statIcon: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 8px',
    background: 'linear-gradient(135deg, #226D68 0%, #2a857e 100%)',
    color: 'white',
    fontSize: 20,
  },
  statNumber: {
    fontWeight: 700,
    color: '#226D68',
    fontSize: '1.5rem',
    marginBottom: theme.spacing(0.5),
  },
  statLabel: {
    color: '#666',
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tableContainer: {
    backgroundColor: 'white',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: '1px solid #e0e0e0',
  },
  actionButton: {
    background: 'linear-gradient(135deg, #226D68 0%, #2a857e 100%)',
    color: 'white',
    borderRadius: '10px',
    padding: theme.spacing(1, 2),
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': {
      background: 'linear-gradient(135deg, #1e615d 0%, #26746e 100%)',
      transform: 'translateY(-1px)',
    },
  },
  exportButton: {
    background: 'linear-gradient(135deg, #dc3545 0%, #e74c3c 100%)',
    color: 'white',
    borderRadius: '10px',
    padding: theme.spacing(1, 2),
    fontWeight: 600,
    textTransform: 'none',
    marginLeft: theme.spacing(1),
    '&:hover': {
      background: 'linear-gradient(135deg, #c82333 0%, #d63031 100%)',
      transform: 'translateY(-1px)',
    },
  },
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '16px',
      padding: theme.spacing(1),
    },
  },
  dialogTitle: {
    background: 'linear-gradient(135deg, #226D68 0%, #2a857e 100%)',
    color: 'white',
    padding: theme.spacing(3),
    '& h2': {
      fontWeight: 600,
    },
  },
  formField: {
    marginBottom: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
    },
  },
  submitButton: {
    background: 'linear-gradient(135deg, #f26522 0%, #ff8c42 100%)',
    color: 'white',
    borderRadius: '12px',
    padding: theme.spacing(1.5),
    fontWeight: 600,
    textTransform: 'none',
    marginTop: theme.spacing(2),
    '&:hover': {
      background: 'linear-gradient(135deg, #e55a1a 0%, #ff7b2e 100%)',
      transform: 'translateY(-1px)',
    },
  },
  chip: {
    fontWeight: 600,
    '&.pending': {
      backgroundColor: '#FFF3CD',
      color: '#856404',
    },
    '&.delivered': {
      backgroundColor: '#D4EDDA',
      color: '#155724',
    },
    '&.cancelled': {
      backgroundColor: '#F8D7DA',
      color: '#721C24',
    },
  },
  loadingContainer: {
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  loadingSpinner: {
    color: '#226D68',
    marginBottom: theme.spacing(2),
  },
  loadingText: {
    color: '#666',
    fontWeight: 600,
  },
}));

// Variables d'environnement
const PHARMACY_NAME = process.env.REACT_APP_PHARMACY_NAME || "PHARMACIE NDIOL FOUTA";
const OWNER_NAME = process.env.REACT_APP_OWNER_NAME || "DR. AL Ousseynou KEITA";
const ADDRESS_LINE1 = process.env.REACT_APP_ADDRESS_LINE1 || "Sor Diagne Route Nationale N 2";
const ADDRESS_LINE2 = process.env.REACT_APP_ADDRESS_LINE2 || "X Médina Bois Saint-Louis - Tél: +221 33 961 08 80";
const BUSINESS_INFO = process.env.REACT_APP_BUSINESS_INFO || "NINEA:007968079 - RC: SN-STL-2020-A-1225";

const CustomLoading = () => {
  const classes = useStyles();
  
  return (
    <Paper className={classes.loadingContainer}>
      <CircularProgress 
        size={60} 
        className={classes.loadingSpinner} 
      />
      <Typography variant="h6" className={classes.loadingText}>
        Chargement des commandes...
      </Typography>
    </Paper>
  );
};

const ListCommandes = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [coms, setCommandes] = useState([]);
  const [fournisseur, setFournisseurs] = useState([]);
  const [nums, setNums] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFournisseur, setSelectedFournisseur] = useState('');

  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    delivered: 0,
    cancelled: 0
  });
  const user = useSelector((state) => state.auth);

  const [state, setStates] = useState({
    open: false,
    editProduct: []
  });

  const [updateProd, setUpdateProd] = useState({
    nomProd: "",
    prixPublic: "",
    dateExpiration: "",
    qtEntrer: "",
    intituleFam: "",
    FormFarmaceutik: "",
    speudo: ""
  });

  const generatePDF = () => {
    try {
      const fileName = generateCommandesPDF(coms, stats);
      toast.success(`PDF "${fileName}" généré avec succès`, {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } catch (error) {
      console.error('Erreur génération PDF:', error);
      toast.error("Erreur lors de la génération du PDF", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  };

  const handleFilters = async (e) => {
    const value = e.target.value;
    setSelectedFournisseur(value);
    
    setLoading(true);
    try {
      let response;
      
      if (value === "") {
        response = await BonService.getBons();
      } else {
        response = await BonService.getBonByFournisseurs(value);
      }
      
      setCommandes(response.data);
      
      setStats({
        total: response.data.length,
        pending: response.data.filter(cmd => !cmd.quan_livree || cmd.quan_livree === 0).length,
        delivered: response.data.filter(cmd => cmd.quan_livree > 0).length,
        cancelled: 0
      });
      
    } catch (error) {
      console.log("Erreur filtrage:", error);
      toast.error("Erreur lors du chargement des commandes", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNumber = async (e, v, r) => {
    if (!v) {
      await refreshCommandes();
      return;
    }
    
    setLoading(true);
    try {
      const response = await BonService.getBonByNumbers(v.idNumero);
      setCommandes(response.data);
    } catch (error) {
      console.log("Erreur filtrage numéro:", error);
      toast.error("Erreur lors du filtrage par numéro", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangeUpdate = (e) => {
    setUpdateProd({ ...updateProd, [e.target.name]: e.target.value });
  };

  const handleClose = () => {
    setStates({ open: false });
  };

  const handleUpdate = async (e) => {
    toast.success("Produit modifié avec succès", {
      position: toast.POSITION.BOTTOM_RIGHT,
    });
    handleClose();
    refreshCommandes();
  };

  const fetchNums = async () => {
    await NumCommandeService.getNumbers()
      .then((response) => {
        setNums(response.data);
      })
      .catch(e => {
        console.log("error numeros" + e);
      });
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [bonsResponse, fournisseursResponse] = await Promise.all([
          BonService.getBons(),
          FournisseurService.getFournisseurs()
        ]);

        setCommandes(bonsResponse.data);
        setFournisseurs(fournisseursResponse.data);
        
        setStats({
          total: bonsResponse.data.length,
          pending: bonsResponse.data.filter(cmd => !cmd.quan_livree || cmd.quan_livree === 0).length,
          delivered: bonsResponse.data.filter(cmd => cmd.quan_livree > 0).length,
          cancelled: 0
        });
      } catch (error) {
        console.log(error);
        toast.error("Erreur lors du chargement des données", {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    fetchNums();
  }, []);

  const refreshCommandes = async () => {
    await BonService.getBons()
      .then((response) => {
        setCommandes(response.data);
      })
      .catch(e => {
        console.log(e);
      });
  };

  const handleOpen = (row) => {
    setStates({ open: true, editProduct: row });
    setUpdateProd({
      ...updateProd,
      nomProd: row[0]?.nomProd,
      prixPublic: row[0]?.prixPublic,
      dateExpiration: row[0]?.dateExpiration,
      qtEntrer: row[0]?.qtEntrer,
      FormFarmaceutik: row[0]?.FormFarmaceutik,
      intituleFam: row[0]?.intituleFam,
      speudo: row[0]?.speudo
    });
  };

  const columns = [
    { 
      title: "ID", 
      field: "idBonDeComdeMat", 
      width: 80,
      headerStyle: { fontWeight: 600 },
    },
    { 
      title: "REFERENCE", 
      field: "numero", 
      editable: 'never',
      headerStyle: { fontWeight: 600 },
    },
    { 
      title: "NOM PRODUIT", 
      field: "nomProd", 
      editable: 'never',
      headerStyle: { fontWeight: 600 },
    },
    { 
      title: "FOURNISSEUR", 
      field: "nomFournisseur", 
      editable: 'never',
      headerStyle: { fontWeight: 600 },
    },
    { 
      title: "PRIX VENTE", 
      field: "prixPublic", 
      render: rowData => `${rowData.prixPublic} Fcfa`,
      headerStyle: { fontWeight: 600 },
    },
    { 
      title: "PRIX UNITAIRE", 
      field: "prixUnitaire", 
      render: rowData => `${rowData.prixUnitaire} Fcfa`,
      headerStyle: { fontWeight: 600 },
    },
    { 
      title: "STATUT", 
      field: "quan_livree", 
      render: rowData => (
        <Chip
          label={rowData.quan_livree > 0 ? "Livrée" : "En attente"}
          className={`${classes.chip} ${rowData.quan_livree > 0 ? 'delivered' : 'pending'}`}
          size="small"
        />
      ),
      headerStyle: { fontWeight: 600 },
    },
    { 
      title: "QUANTITÉ", 
      field: "quan_livree", 
      headerStyle: { fontWeight: 600 },
    },
    { 
      title: "CREATEUR", 
      field: "speudo", 
      editable: 'never',
      headerStyle: { fontWeight: 600 },
    },
  ];

  return (
    <div className={classes.root}>
      <Container maxWidth="xl">
        {/* Filtres */}
        <Paper className={classes.filtersContainer}>
          <Box className={classes.filterSection}>
            <FilterList color="primary" />
            <Typography variant="h6" style={{ color: '#226D68', fontWeight: 600 }}>
              Filtres
            </Typography>
            
            <Autocomplete
              className={classes.autocomplete}
              options={nums}
              getOptionLabel={(option) => option.numero}
              onChange={handleNumber}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Numéro de commande"
                  variant="outlined"
                  size="small"
                />
              )}
            />

            <FormControl variant="outlined" className={classes.select} size="small">
              <InputLabel>Fournisseur</InputLabel>
              <Select
                onChange={handleFilters}
                label="Fournisseur"
              >
                <MenuItem value="">
                  <em>Tous les fournisseurs</em>
                </MenuItem>
                {fournisseur.map((f) => (
                  <MenuItem key={f.idFournisseur} value={f.idFournisseur}>
                    {f.nomFournisseur}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <IconButton 
              onClick={refreshCommandes}
              style={{ color: '#226D68' }}
            >
              <Refresh />
            </IconButton>

            {/* Bouton d'export PDF */}
            <Button
              className={classes.exportButton}
              startIcon={<PictureAsPdf />}
              onClick={generatePDF}
              size="small"
            >
              Exporter PDF
            </Button>
          </Box>
        </Paper>

        {/* Tableau */}
        <Paper className={classes.tableContainer}>
          {loading ? (
            <CustomLoading />
          ) : (
            <MaterialTable
              columns={columns}
              data={coms}
              editable={{
                onRowUpdate: (newRow, oldRow) => new Promise((resolve, reject) => {
                  if (user?.rol === 'gerant') {
                    toast.error("Vous n'avez pas les droits nécessaires", {
                      position: toast.POSITION.BOTTOM_RIGHT,
                    });
                    resolve();
                    return;
                  }

                  BonService.updateBon(newRow, oldRow.idBonDeComdeMat)
                    .then((response) => {
                      if (response?.data?.message === 'Ce type de client ne peut etre modifié') {
                        toast.error(response?.data?.message);
                      } else {
                        toast.success("Commande modifiée avec succès");
                      }
                      refreshCommandes();
                    })
                    .catch((error) => {
                      toast.error("Erreur lors de la modification");
                    });
                  resolve();
                }),
                onRowDelete: (selectedRow) => new Promise((resolve, reject) => {
                  if (user?.rol === 'gerant') {
                    toast.error("Vous n'avez pas les droits nécessaires");
                    resolve();
                    return;
                  }
                  BonService.deleteBon(selectedRow.idBonDeComdeMat)
                    .then((response) => {
                      toast.success("Commande supprimée avec succès");
                      refreshCommandes();
                    })
                    .catch((error) => {
                      toast.error("Erreur lors de la suppression");
                    });
                  resolve();
                })
              }}
              
              options={{
                sorting: true,
                search: true,
                searchFieldVariant: "outlined",
                filtering: false,
                paging: true,
                pageSize: 10,
                pageSizeOptions: [5, 10, 20],
                paginationType: "stepped",
                showFirstLastPageButtons: true,
                exportButton: true,
                exportFileName: "Commandes",
                actionsColumnIndex: -1,
                headerStyle: {
                  backgroundColor: '#226D68',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '14px',
                },
                rowStyle: (data, index) => ({
                  backgroundColor: index % 2 === 0 ? '#f8f9fa' : 'white',
                }),
              }}
              title={
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="h6" style={{ color: '#226D68', fontWeight: 600 }}>
                    Liste des Commandes
                  </Typography>
                  <Button
                    className={classes.exportButton}
                    startIcon={<PictureAsPdf />}
                    onClick={generatePDF}
                    size="small"
                  >
                    PDF
                  </Button>
                </Box>
              }
              localization={{
                body: {
                  editTooltip: 'Modifier la commande',
                  deleteTooltip: 'Supprimer la commande',
                  editRow: {
                    deleteText: 'Êtes-vous sûr de vouloir supprimer cette commande ?',
                    cancelTooltip: 'Annuler',
                    saveTooltip: 'Sauvegarder'
                  }
                }
              }}
            />
          )}
        </Paper>

        {/* Dialog de modification */}
        <Dialog
          open={state.open}
          onClose={handleClose}
          className={classes.dialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle className={classes.dialogTitle}>
            Modifier le Produit
          </DialogTitle>
          <DialogContent>
            {state.editProduct && (
              <Box pt={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      className={classes.formField}
                      label="Nom du produit"
                      name="nomProd"
                      value={updateProd.nomProd}
                      onChange={handleChangeUpdate}
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      className={classes.formField}
                      label="Date d'expiration"
                      name="dateExpiration"
                      type="date"
                      value={updateProd.dateExpiration}
                      onChange={handleChangeUpdate}
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      className={classes.formField}
                      label="Prix public"
                      name="prixPublic"
                      value={updateProd.prixPublic}
                      onChange={handleChangeUpdate}
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      className={classes.formField}
                      label="Quantité"
                      name="qtEntrer"
                      value={updateProd.qtEntrer}
                      onChange={handleChangeUpdate}
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      className={classes.submitButton}
                      onClick={handleUpdate}
                      fullWidth
                      size="large"
                    >
                      Valider les modifications
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </Container>
    </div>
  );
};

export default ListCommandes;